import * as React from 'react'
import { cn } from '../../lib/utils'
import { Logo } from './Logo'
import { Button } from '../ui/button'
import { ScrollArea } from '../ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface SidebarItem {
  path: string
  name: string
  icon: React.ReactNode
}

export interface SidebarProps {
  items: SidebarItem[]
  collapsed: boolean
  onCollapse: (collapsed: boolean) => void
  currentPath: string
  onNavigate: (path: string) => void
  logo?: React.ReactNode
  title?: string
  footer?: React.ReactNode
}

export function Sidebar({
  items,
  collapsed,
  onCollapse,
  currentPath,
  onNavigate,
  logo,
  title = 'IntelliMon',
  footer,
}: SidebarProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen flex flex-col border-r bg-sidebar transition-all duration-300',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center border-b px-4">
          <div className="flex items-center gap-3">
            {logo || <Logo size="sm" />}
            {!collapsed && (
              <span className="text-lg font-semibold tracking-tight">{title}</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="flex flex-col gap-1 px-2">
            {items.map((item) => {
              const isActive = currentPath === item.path ||
                (item.path !== '/' && currentPath.startsWith(item.path))

              const button = (
                <Button
                  key={item.path}
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={cn(
                    'w-full justify-start gap-3 transition-colors',
                    isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium',
                    collapsed && 'justify-center px-2'
                  )}
                  onClick={() => onNavigate(item.path)}
                >
                  <span className={cn(
                    '[&_svg]:h-5 [&_svg]:w-5',
                    isActive && 'text-primary'
                  )}>
                    {item.icon}
                  </span>
                  {!collapsed && <span>{item.name}</span>}
                </Button>
              )

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                )
              }

              return button
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        {footer && (
          <div className="border-t p-4">
            {footer}
          </div>
        )}

        {/* Collapse Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-20 h-6 w-6 rounded-full border bg-background shadow-md"
          onClick={() => onCollapse(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </Button>
      </aside>
    </TooltipProvider>
  )
}
